import { type ReactNode } from 'react';
import { type BaseTag } from '@hungryroot/rest';
import { ControlSelect, type Quiz } from '../main';
import { type QuizTagKeys } from '../quiz-hooks';

export type ControlChildren = (params: {
  tag: BaseTag;
  active: boolean;
  index: number;
}) => ReactNode;

type RenderTagsFilter =
  | {
      filterIds?: number[];
      tagExclusions?: never;
    }
  | { tagExclusions?: number[]; filterIds?: never };

export function renderTags<K extends QuizTagKeys>(
  question: K,
  quiz: Quiz,
  tagGroupTags: BaseTag[],
  children: ControlChildren,
  filterOptions: RenderTagsFilter
) {
  let tags = tagGroupTags;
  if ('filterIds' in filterOptions && filterOptions.filterIds) {
    tags = tagGroupTags.filter(
      (tag) => filterOptions.filterIds?.includes(tag.id)
    );
  }

  if ('tagExclusions' in filterOptions && filterOptions.tagExclusions) {
    tags = tagGroupTags.filter(
      (tag) => !filterOptions?.tagExclusions?.includes(tag.id)
    );
  }

  const value = quiz[question];
  return tags.map((tag, i) => {
    const { id: tagId } = tag;
    const child = children({
      tag,
      active:
        value === null
          ? false
          : Array.isArray(value)
            ? value.includes(tagId)
            : value === tagId,
      index: i,
    });

    return (
      child !== undefined && (
        <ControlSelect.Option key={tagId} id={tagId} label={tag.name}>
          {child}
        </ControlSelect.Option>
      )
    );
  });
}

export const getSelectedKeys = <K extends QuizTagKeys>(
  quiz: Quiz,
  question: K
) => {
  if (quiz[question] === null) {
    return;
  }

  return Array.isArray(quiz[question]) ? quiz[question] : [quiz[question]];
};

export type OnItemSelection = {
  itemValue: number;
  handler: () => void;
};
